import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Routes from '../configuration/routes/RouteList';
import {
  useUploadSessionStore,
  type CloudProvider,
  type GroqProcessingMode,
  type LocalProvider,
  type ProviderMode,
  type SelectedUploadFile,
} from '../stores/uploadSessionStore';
import {
  listPersistedNotes,
  loadCachedNotes,
  makeTranscriptionNoteId,
  savePersistedNote,
  type PersistedTranscriptionNote,
} from '../services/persistedNotes';

type TranscriptionNote = PersistedTranscriptionNote;

type UploadSettings = {
  mode?: ProviderMode;
  cloudProvider?: CloudProvider;
  groqProcessingMode?: GroqProcessingMode;
  cloudModel?: string;
  localProvider?: LocalProvider;
  localModel?: string;
  localParakeetModel?: string;
  language?: string;
  apiKeys?: Record<string, string>;
  customUrl?: string;
};

type CloudProviderConfig = {
  id: CloudProvider;
  name: string;
  baseUrl: string;
  keyUrl: string;
  models: { id: string; name: string; description: string }[];
  recommended?: boolean;
};

const CLOUD_PROVIDERS: CloudProviderConfig[] = [
  {
    id: 'openwhispr',
    name: 'OpenWhispr Cloud',
    baseUrl: '',
    keyUrl: '',
    models: [
      {
        id: 'default',
        name: 'OpenWhispr Cloud Engine',
        description: 'Automatic transcription through the OpenWhispr upload pipeline',
      },
    ],
  },
  {
    id: 'openai',
    name: 'OpenAI',
    baseUrl: 'https://api.openai.com/v1',
    keyUrl: 'https://platform.openai.com/api-keys',
    models: [
      {
        id: 'gpt-4o-mini-transcribe',
        name: 'GPT-4o Mini Transcribe',
        description: 'Fast and accurate transcription',
      },
      {
        id: 'gpt-4o-transcribe',
        name: 'GPT-4o Transcribe',
        description: 'Most accurate transcription',
      },
      {
        id: 'whisper-1',
        name: 'Whisper',
        description: 'Original Whisper model',
      },
    ],
  },
  {
    id: 'groq',
    name: 'Groq',
    baseUrl: 'https://api.groq.com/openai/v1',
    keyUrl: 'https://console.groq.com/keys',
    recommended: true,
    models: [
      {
        id: 'whisper-large-v3',
        name: 'Whisper Large v3',
        description: 'High accuracy speech recognition',
      },
      {
        id: 'whisper-large-v3-turbo',
        name: 'Whisper Large v3 Turbo',
        description: '216x real-time speed',
      },
    ],
  },
  {
    id: 'mistral',
    name: 'Mistral',
    baseUrl: 'https://api.mistral.ai/v1',
    keyUrl: 'https://console.mistral.ai/api-keys',
    models: [
      {
        id: 'voxtral-mini-latest',
        name: 'Voxtral Mini',
        description: 'Fast multilingual transcription',
      },
    ],
  },
  {
    id: 'custom',
    name: 'Custom',
    baseUrl: '',
    keyUrl: '',
    models: [
      {
        id: 'whisper-1',
        name: 'Whisper',
        description: 'Default model when pointing at a custom transcription endpoint',
      },
    ],
  },
];

const LOCAL_WHISPER_MODELS = [
  { id: 'tiny', name: 'Tiny', size: '75 MB', description: 'Fastest, lower quality' },
  { id: 'base', name: 'Base', size: '142 MB', description: 'Good balance', recommended: true },
  { id: 'small', name: 'Small', size: '466 MB', description: 'Better quality, slower' },
  { id: 'medium', name: 'Medium', size: '1.5 GB', description: 'High quality' },
  { id: 'large', name: 'Large', size: '3 GB', description: 'Best quality, slowest' },
  { id: 'turbo', name: 'Turbo', size: '1.6 GB', description: 'Fast with good quality' },
];

const LOCAL_PARAKEET_MODELS = [
  {
    id: 'parakeet-tdt-0.6b-v3',
    name: 'Parakeet TDT 0.6B',
    size: '680 MB',
    description: 'Fast multilingual ASR with auto language detection (25 languages)',
    recommended: true,
  },
];

const SUPPORTED_FORMATS = ['mp3', 'wav', 'm4a', 'webm', 'ogg', 'flac', 'aac', 'mp4'];
const SETTINGS_KEY = 'niuniu_transcription_settings';
const BYOK_MAX_FILE_SIZE = 25 * 1024 * 1024;
const OPENWHISPR_CLOUD_MAX_FILE_SIZE = 500 * 1024 * 1024;

function loadSettings(): UploadSettings {
  try {
    return JSON.parse(localStorage.getItem(SETTINGS_KEY) || '{}');
  } catch {
    return {};
  }
}

function updateSettings(patch: Partial<UploadSettings>) {
  localStorage.setItem(SETTINGS_KEY, JSON.stringify({ ...loadSettings(), ...patch }));
}

function formatDuration(sec?: number) {
  if (!sec) return '';
  const minutes = Math.floor(sec / 60);
  const seconds = Math.round(sec % 60);
  return minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
}

function formatFileSize(bytes?: number) {
  if (!bytes) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

function getCloudProviderConfig(provider: CloudProvider) {
  return CLOUD_PROVIDERS.find(item => item.id === provider) ?? CLOUD_PROVIDERS[0];
}

function getDefaultCloudModel(provider: CloudProvider) {
  return getCloudProviderConfig(provider).models[0]?.id ?? 'whisper-1';
}

function normalizeCustomBaseUrl(url: string) {
  const trimmed = url.trim();
  if (!trimmed) return '';
  return trimmed.replace(/\/audio\/transcriptions\/?$/i, '').replace(/\/+$/, '');
}

function ProviderBadge({ mode, cloud }: { mode: ProviderMode; cloud?: CloudProvider }) {
  const label = mode === 'local' ? 'LOCAL' : (cloud || 'cloud').toUpperCase();
  const bgMap: Record<string, string> = {
    local: '#4caf50',
    groq: '#f5821f',
    mistral: '#7c3aed',
    openai: '#10a37f',
    openwhispr: 'var(--color-primary)',
    custom: 'var(--color-muted-foreground)',
  };
  const bg = mode === 'local' ? bgMap.local : (cloud ? bgMap[cloud] : bgMap.openwhispr) ?? bgMap.openwhispr;

  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      fontSize: '10px',
      fontWeight: 700,
      padding: '2px 8px',
      borderRadius: '4px',
      background: bg,
      color: '#fff',
      letterSpacing: '0.04em',
    }}>
      {label}
    </span>
  );
}

function SettingsPanel({
  mode,
  setMode,
  cloudProvider,
  setCloudProvider,
  groqProcessingMode,
  setGroqProcessingMode,
  cloudModel,
  setCloudModel,
  localProvider,
  setLocalProvider,
  localModel,
  setLocalModel,
  localParakeetModel,
  setLocalParakeetModel,
  apiKeys,
  setApiKey,
  customUrl,
  setCustomUrl,
  language,
  setLanguage,
  runtimeHint,
}: {
  mode: ProviderMode;
  setMode: (mode: ProviderMode) => void;
  cloudProvider: CloudProvider;
  setCloudProvider: (provider: CloudProvider) => void;
  groqProcessingMode: GroqProcessingMode;
  setGroqProcessingMode: (mode: GroqProcessingMode) => void;
  cloudModel: string;
  setCloudModel: (model: string) => void;
  localProvider: LocalProvider;
  setLocalProvider: (provider: LocalProvider) => void;
  localModel: string;
  setLocalModel: (model: string) => void;
  localParakeetModel: string;
  setLocalParakeetModel: (model: string) => void;
  apiKeys: Record<string, string>;
  setApiKey: (provider: string, key: string) => void;
  customUrl: string;
  setCustomUrl: (url: string) => void;
  language: string;
  setLanguage: (language: string) => void;
  runtimeHint?: string;
}) {
  const [showKey, setShowKey] = useState<Record<string, boolean>>({});
  const provider = getCloudProviderConfig(cloudProvider);

  const fieldLabel = (text: string) => (
    <div style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--color-muted-foreground)', marginBottom: '8px', opacity: 0.75 }}>
      {text}
    </div>
  );

  return (
    <div style={{ fontSize: '13px', display: 'flex', flexDirection: 'column', gap: '22px' }}>

      {/* Mode toggle */}
      <div>
        {fieldLabel('Mode')}
        <div style={{
          display: 'flex',
          borderRadius: '8px',
          overflow: 'hidden',
          border: '1px solid var(--color-border-subtle)',
          background: 'var(--color-surface-1)',
          padding: '3px',
          gap: '2px',
        }}>
          {(['cloud', 'local'] as ProviderMode[]).map(currentMode => (
            <button
              key={currentMode}
              onClick={() => setMode(currentMode)}
              style={{
                flex: 1,
                padding: '8px 0',
                cursor: 'pointer',
                border: 'none',
                borderRadius: '6px',
                fontSize: '13px',
                fontWeight: mode === currentMode ? 600 : 400,
                background: mode === currentMode ? 'var(--color-surface-raised)' : 'transparent',
                color: mode === currentMode ? 'var(--color-foreground)' : 'var(--color-muted-foreground)',
                boxShadow: mode === currentMode ? 'var(--shadow-card)' : 'none',
                transition: 'all 150ms ease',
              }}
            >
              {currentMode === 'cloud' ? '☁ Cloud' : '⚙ Local'}
            </button>
          ))}
        </div>
      </div>

      {mode === 'cloud' ? (
        <>
          {/* Cloud provider list */}
          <div>
            {fieldLabel('Provider')}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {CLOUD_PROVIDERS.map(item => (
                <div
                  key={item.id}
                  onClick={() => {
                    setCloudProvider(item.id);
                    if (item.id !== 'custom') {
                      setCloudModel(item.models[0]?.id ?? 'whisper-1');
                    } else if (!cloudModel.trim()) {
                      setCloudModel('whisper-1');
                    }
                  }}
                  className="provider-card"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '9px 12px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    border: `1px solid ${cloudProvider === item.id ? 'var(--color-primary)' : 'var(--color-border-subtle)'}`,
                    background: cloudProvider === item.id ? 'oklch(from var(--color-primary) l c h / 0.07)' : 'transparent',
                    transition: 'border-color 120ms ease, background 120ms ease',
                  }}
                >
                  <span style={{ fontWeight: cloudProvider === item.id ? 600 : 400, fontSize: '13px', color: cloudProvider === item.id ? 'var(--color-foreground)' : 'var(--color-muted-foreground)' }}>
                    {item.name}
                  </span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    {item.recommended && (
                      <span style={{ fontSize: '9px', fontWeight: 700, padding: '2px 5px', borderRadius: '3px', background: '#f5821f', color: '#fff', letterSpacing: '0.03em' }}>
                        TOP
                      </span>
                    )}
                    {cloudProvider === item.id && (
                      <span style={{ color: 'var(--color-primary)', fontSize: '12px', lineHeight: 1 }}>✓</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Custom endpoint fields */}
          {cloudProvider === 'custom' ? (
            <>
              <div>
                {fieldLabel('Endpoint URL')}
                <input
                  type="text"
                  placeholder="https://your-endpoint.example.com/v1"
                  value={customUrl}
                  onChange={event => setCustomUrl(event.target.value)}
                  style={{ width: '100%', boxSizing: 'border-box', fontSize: '12px' }}
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  {fieldLabel('API Key (Optional)')}
                  <span
                    className="text-[11px] cursor-pointer"
                    style={{ opacity: 0.6 }}
                    onClick={() => setShowKey(state => ({ ...state, custom: !state.custom }))}
                  >
                    {showKey.custom ? 'hide' : 'show'}
                  </span>
                </div>
                <input
                  type={showKey.custom ? 'text' : 'password'}
                  value={apiKeys.custom || ''}
                  onChange={event => setApiKey('custom', event.target.value)}
                  placeholder="Optional bearer token"
                  style={{ width: '100%', boxSizing: 'border-box', fontSize: '12px' }}
                />
              </div>
              <div>
                {fieldLabel('Model')}
                <input
                  type="text"
                  value={cloudModel}
                  onChange={event => setCloudModel(event.target.value)}
                  placeholder="whisper-1"
                  style={{ width: '100%', boxSizing: 'border-box', fontSize: '12px' }}
                />
              </div>
            </>
          ) : (
            <>
              {/* API key for non-openwhispr providers */}
              {cloudProvider !== 'openwhispr' && (
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    {fieldLabel('API Key')}
                    <div className="flex items-center gap-2">
                      <span
                        className="text-[11px] cursor-pointer"
                        style={{ opacity: 0.6 }}
                        onClick={() => setShowKey(state => ({ ...state, [cloudProvider]: !state[cloudProvider] }))}
                      >
                        {showKey[cloudProvider] ? 'hide' : 'show'}
                      </span>
                      {provider.keyUrl && (
                        <a href={provider.keyUrl} target="_blank" rel="noreferrer"
                          className="text-[11px]" style={{ opacity: 0.6, color: 'inherit' }}>
                          Get key ↗
                        </a>
                      )}
                    </div>
                  </div>
                  <input
                    type={showKey[cloudProvider] ? 'text' : 'password'}
                    value={apiKeys[cloudProvider] || ''}
                    onChange={event => setApiKey(cloudProvider, event.target.value)}
                    placeholder={cloudProvider === 'groq' ? 'gsk_...' : 'sk-...'}
                    style={{ width: '100%', boxSizing: 'border-box', fontSize: '12px' }}
                  />
                </div>
              )}
              <div>
                {fieldLabel('Model')}
                <select
                  value={cloudModel}
                  onChange={event => setCloudModel(event.target.value)}
                  style={{ width: '100%' }}
                >
                  {provider.models.map(model => (
                    <option key={model.id} value={model.id}>
                      {model.name}
                    </option>
                  ))}
                </select>
              </div>
              {cloudProvider === 'groq' && (
                <div>
                  {fieldLabel('Groq Route')}
                  <div style={{
                    display: 'flex',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    border: '1px solid var(--color-border-subtle)',
                    background: 'var(--color-surface-1)',
                    padding: '3px',
                    gap: '2px',
                  }}>
                    {([
                      ['auto', 'Auto'],
                      ['direct', 'Direct'],
                      ['batch', 'Batch'],
                    ] as [GroqProcessingMode, string][]).map(([routeMode, label]) => (
                      <button
                        key={routeMode}
                        onClick={() => setGroqProcessingMode(routeMode)}
                        style={{
                          flex: 1,
                          padding: '8px 0',
                          cursor: 'pointer',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '13px',
                          fontWeight: groqProcessingMode === routeMode ? 600 : 400,
                          background: groqProcessingMode === routeMode ? 'var(--color-surface-raised)' : 'transparent',
                          color: groqProcessingMode === routeMode ? 'var(--color-foreground)' : 'var(--color-muted-foreground)',
                          boxShadow: groqProcessingMode === routeMode ? 'var(--shadow-card)' : 'none',
                          transition: 'all 150ms ease',
                        }}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                  <p style={{ fontSize: '10px', marginTop: '6px', color: 'var(--color-muted-foreground)', opacity: 0.65, lineHeight: 1.55 }}>
                    {groqProcessingMode === 'auto'
                      ? 'Auto keeps small Groq files synchronous and hands larger ones to the backend worker queue.'
                      : groqProcessingMode === 'direct'
                        ? 'Direct keeps Groq work in the browser-managed path, including local optimization and client-side chunking when needed.'
                        : 'Batch always uploads once and lets the backend worker queue split and transcribe the file.'}
                  </p>
                </div>
              )}
            </>
          )}
        </>
      ) : (
        <>
          {/* Local engine selector */}
          <div>
            {fieldLabel('Engine')}
            <div style={{
              display: 'flex',
              borderRadius: '8px',
              overflow: 'hidden',
              border: '1px solid var(--color-border-subtle)',
              background: 'var(--color-surface-1)',
              padding: '3px',
              gap: '2px',
            }}>
              {(['whisper', 'nvidia'] as LocalProvider[]).map(providerId => (
                <button
                  key={providerId}
                  onClick={() => setLocalProvider(providerId)}
                  style={{
                    flex: 1,
                    padding: '8px 0',
                    cursor: 'pointer',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '13px',
                    fontWeight: localProvider === providerId ? 600 : 400,
                    background: localProvider === providerId ? 'var(--color-surface-raised)' : 'transparent',
                    color: localProvider === providerId ? 'var(--color-foreground)' : 'var(--color-muted-foreground)',
                    boxShadow: localProvider === providerId ? 'var(--shadow-card)' : 'none',
                    transition: 'all 150ms ease',
                  }}
                >
                  {providerId === 'whisper' ? 'Whisper' : 'Parakeet'}
                </button>
              ))}
            </div>
            <p style={{ fontSize: '10px', marginTop: '6px', color: 'var(--color-muted-foreground)', opacity: 0.6 }}>
              Runs locally · works offline
            </p>
          </div>

          {/* Local model list */}
          <div>
            {fieldLabel(localProvider === 'whisper' ? 'Whisper Model' : 'Parakeet Model')}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {(localProvider === 'whisper' ? LOCAL_WHISPER_MODELS : LOCAL_PARAKEET_MODELS).map(model => {
                const selectedModel = localProvider === 'whisper' ? localModel : localParakeetModel;
                const selectModel = localProvider === 'whisper' ? setLocalModel : setLocalParakeetModel;
                const isSelected = selectedModel === model.id;

                return (
                  <div
                    key={model.id}
                    onClick={() => selectModel(model.id)}
                    className="provider-card"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '9px 12px',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      border: `1px solid ${isSelected ? 'var(--color-primary)' : 'var(--color-border-subtle)'}`,
                      background: isSelected ? 'oklch(from var(--color-primary) l c h / 0.07)' : 'transparent',
                      transition: 'border-color 120ms ease, background 120ms ease',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
                      <span style={{ fontWeight: isSelected ? 600 : 400, fontSize: '13px', color: isSelected ? 'var(--color-foreground)' : 'var(--color-muted-foreground)' }}>
                        {model.name}
                      </span>
                      {model.recommended && (
                        <span style={{ fontSize: '9px', fontWeight: 700, padding: '2px 5px', borderRadius: '3px', background: '#4caf50', color: '#fff', flexShrink: 0 }}>
                          ★
                        </span>
                      )}
                    </div>
                    <span style={{ fontSize: '10px', color: 'var(--color-muted-foreground)', flexShrink: 0, marginLeft: '8px', opacity: 0.6 }}>
                      {model.size}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}

      {/* Language */}
      <div>
        {fieldLabel('Language')}
        <select
          value={language}
          onChange={event => setLanguage(event.target.value)}
          style={{ width: '100%' }}
        >
          <option value="auto">Auto-detect</option>
          <option value="zh">Chinese</option>
          <option value="en">English</option>
          <option value="ja">Japanese</option>
          <option value="ko">Korean</option>
          <option value="es">Spanish</option>
          <option value="fr">French</option>
          <option value="de">German</option>
        </select>
      </div>

      {/* Runtime hint */}
      {runtimeHint && (
        <p style={{ fontSize: '11px', lineHeight: 1.6, color: 'var(--color-muted-foreground)', opacity: 0.55 }}>
          {runtimeHint}
        </p>
      )}
    </div>
  );
}

const Upload = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const initialSettings = loadSettings();

  const [mode, setMode] = useState<ProviderMode>(initialSettings.mode || 'local');
  const [cloudProvider, setCloudProvider] = useState<CloudProvider>(
    initialSettings.cloudProvider || 'openwhispr',
  );
  const [groqProcessingMode, setGroqProcessingMode] = useState<GroqProcessingMode>(
    initialSettings.groqProcessingMode || 'auto',
  );
  const [cloudModel, setCloudModel] = useState(
    initialSettings.cloudModel || getDefaultCloudModel(initialSettings.cloudProvider || 'openwhispr'),
  );
  const [localProvider, setLocalProvider] = useState<LocalProvider>(
    initialSettings.localProvider || 'whisper',
  );
  const [localModel, setLocalModel] = useState(initialSettings.localModel || 'turbo');
  const [localParakeetModel, setLocalParakeetModel] = useState(
    initialSettings.localParakeetModel || 'parakeet-tdt-0.6b-v3',
  );
  const [language, setLanguage] = useState(initialSettings.language || 'auto');
  const [apiKeys, setApiKeysState] = useState<Record<string, string>>(initialSettings.apiKeys || {});
  const [customUrl, setCustomUrl] = useState(initialSettings.customUrl || '');
  const [dragging, setDragging] = useState(false);
  const [folders, setFolders] = useState<string[]>([]);
  const [isSavingNote, setIsSavingNote] = useState(false);
  const [noteSaveError, setNoteSaveError] = useState('');
  const autoSaveNoteIdRef = useRef<string | null>(null);

  const {
    selectedFile,
    status,
    errorMsg,
    progress,
    transcribingDetail,
    audioDurationSec,
    noteTitle,
    noteText,
    selectedFolder,
    savedNoteId,
    prepareSelectedFile,
    setUploadError,
    resetUpload,
    setNoteTitle,
    setNoteText,
    setSelectedFolder,
    setSavedNoteId,
    transcribe,
  } = useUploadSessionStore();

  const cloudProviderConfig = useMemo(
    () => getCloudProviderConfig(cloudProvider),
    [cloudProvider],
  );
  const activeLocalModel = localProvider === 'whisper' ? localModel : localParakeetModel;
  const activeApiKey = apiKeys[cloudProvider] || '';
  const normalizedCustomUrl = useMemo(() => normalizeCustomBaseUrl(customUrl), [customUrl]);

  const isLimitedByokProvider =
    mode === 'cloud' &&
    (cloudProvider === 'openai' || cloudProvider === 'groq' || cloudProvider === 'mistral');
  const byokNeedsOptimization =
    !!selectedFile && isLimitedByokProvider && selectedFile.sizeBytes > BYOK_MAX_FILE_SIZE;
  const openwhisprTooLarge =
    !!selectedFile &&
    mode === 'cloud' &&
    cloudProvider === 'openwhispr' &&
    selectedFile.sizeBytes > OPENWHISPR_CLOUD_MAX_FILE_SIZE;

  const missingConfigReason = useMemo(() => {
    if (mode !== 'cloud') return '';
    if (cloudProvider === 'openwhispr') return '';
    if (cloudProvider === 'custom') {
      return normalizedCustomUrl ? '' : 'Custom endpoint URL is required.';
    }
    return activeApiKey.trim() ? '' : `${cloudProviderConfig.name} API key is required.`;
  }, [activeApiKey, cloudProvider, cloudProviderConfig.name, mode, normalizedCustomUrl]);

  const activeModelLabel = useMemo(() => {
    if (mode === 'local') {
      if (localProvider === 'nvidia') {
        const model = LOCAL_PARAKEET_MODELS.find(item => item.id === localParakeetModel);
        return `Parakeet - ${model?.name || localParakeetModel}`;
      }
      const model = LOCAL_WHISPER_MODELS.find(item => item.id === localModel);
      return `Whisper - ${model?.name || localModel}`;
    }

    if (cloudProvider === 'custom') {
      return `Custom - ${cloudModel || 'whisper-1'}`;
    }

    const model = cloudProviderConfig.models.find(item => item.id === cloudModel);
    return `${cloudProviderConfig.name} - ${model?.name || cloudModel}`;
  }, [
    cloudModel,
    cloudProvider,
    cloudProviderConfig.models,
    cloudProviderConfig.name,
    localModel,
    localParakeetModel,
    localProvider,
    mode,
  ]);

  const runtimeHint = useMemo(() => {
    if (mode === 'local') {
      return 'Local mode in this web app still runs through your backend transcription service.';
    }

    if (cloudProvider === 'custom') {
      return 'Custom mode forwards requests to your own OpenAI-compatible transcription endpoint.';
    }

    if (cloudProvider === 'groq') {
      if (groqProcessingMode === 'direct') {
        return 'Groq Direct keeps the browser in charge. Small files stay synchronous, and large ones remain on the local optimization or client chunking path.';
      }

      if (groqProcessingMode === 'batch') {
        return 'Groq Batch always uploads the file once and lets the backend job queue handle splitting, progress tracking, and final note saving.';
      }

      return 'Groq Auto keeps small files synchronous, while larger ones are handed to a backend batch job so your browser does not have to chunk multi-hour audio on its own.';
    }

    return 'This upload page maps OpenWhispr-style parameters onto your current web backend.';
  }, [cloudProvider, groqProcessingMode, mode]);

  const canTranscribe =
    !!selectedFile && !missingConfigReason && !openwhisprTooLarge;

  useEffect(() => {
    updateSettings({ mode });
  }, [mode]);

  useEffect(() => {
    updateSettings({ cloudProvider });
  }, [cloudProvider]);

  useEffect(() => {
    updateSettings({ groqProcessingMode });
  }, [groqProcessingMode]);

  useEffect(() => {
    updateSettings({ cloudModel });
  }, [cloudModel]);

  useEffect(() => {
    updateSettings({ localProvider });
  }, [localProvider]);

  useEffect(() => {
    updateSettings({ localModel });
  }, [localModel]);

  useEffect(() => {
    updateSettings({ localParakeetModel });
  }, [localParakeetModel]);

  useEffect(() => {
    updateSettings({ language });
  }, [language]);

  useEffect(() => {
    updateSettings({ customUrl });
  }, [customUrl]);

  useEffect(() => {
    updateSettings({ apiKeys });
  }, [apiKeys]);

  useEffect(() => {
    if (cloudProvider === 'custom') {
      if (!cloudModel.trim()) {
        setCloudModel('whisper-1');
      }
      return;
    }

    if (!cloudProviderConfig.models.some(model => model.id === cloudModel)) {
      setCloudModel(cloudProviderConfig.models[0]?.id ?? 'whisper-1');
    }
  }, [cloudModel, cloudProvider, cloudProviderConfig.models]);

  const setApiKey = useCallback((provider: string, key: string) => {
    setApiKeysState(state => ({ ...state, [provider]: key }));
  }, []);

  const syncNotesState = useCallback(async () => {
    const loadedNotes = await listPersistedNotes().catch(() => loadCachedNotes());
    setFolders(
      [...new Set(loadedNotes.map(item => item.folder).filter(Boolean) as string[])].sort(),
    );
  }, []);

  useEffect(() => {
    void syncNotesState();
  }, [syncNotesState]);

  useEffect(() => {
    if (status === 'idle' || status === 'selected' || status === 'transcribing') {
      autoSaveNoteIdRef.current = null;
      setIsSavingNote(false);
      setNoteSaveError('');
    }
  }, [status]);

  const buildNoteDraft = useCallback(
    (noteId: string): TranscriptionNote | null => {
      if (!selectedFile || !noteText) {
        return null;
      }

      return {
        id: noteId,
        title: noteTitle || selectedFile.name.replace(/\.[^.]+$/, ''),
        text: noteText,
        fileName: selectedFile.name,
        providerMode: mode,
        cloudProvider: mode === 'cloud' ? cloudProvider : undefined,
        model: mode === 'cloud' ? cloudModel : activeLocalModel,
        audioDurationSec,
        createdAt: new Date().toISOString(),
        folder: selectedFolder || undefined,
      };
    },
    [
      activeLocalModel,
      audioDurationSec,
      cloudModel,
      cloudProvider,
      mode,
      noteText,
      noteTitle,
      selectedFile,
      selectedFolder,
    ],
  );

  const persistCurrentNote = useCallback(async () => {
    const noteId = autoSaveNoteIdRef.current ?? makeTranscriptionNoteId();
    autoSaveNoteIdRef.current = noteId;

    const note = buildNoteDraft(noteId);
    if (!note) {
      return null;
    }

    setIsSavingNote(true);
    setNoteSaveError('');

    try {
      const saved = await savePersistedNote(note);
      setSavedNoteId(saved.id);
      await syncNotesState();
      return saved;
    } catch (error) {
      setNoteSaveError(
        error instanceof Error
          ? error.message
          : 'The transcription finished, but saving the note to the database failed.',
      );
      return null;
    } finally {
      setIsSavingNote(false);
    }
  }, [buildNoteDraft, setSavedNoteId, syncNotesState]);

  useEffect(() => {
    if (status !== 'complete' || !selectedFile || !noteText || savedNoteId || isSavingNote) return;

    void persistCurrentNote();
  }, [isSavingNote, noteText, persistCurrentNote, savedNoteId, selectedFile, status]);

  const handleFileSelection = useCallback((nextFile: SelectedUploadFile) => {
    const ext = nextFile.name.split('.').pop()?.toLowerCase() || '';
    if (!SUPPORTED_FORMATS.includes(ext)) {
      setUploadError(`Unsupported format .${ext}. Supported: ${SUPPORTED_FORMATS.join(', ')}`);
      return;
    }

    prepareSelectedFile(nextFile);
    setDragging(false);
  }, [prepareSelectedFile, setUploadError]);


  const handleBrowserInput = useCallback(
    (browserFile: File) => {
      handleFileSelection({
        name: browserFile.name,
        sizeBytes: browserFile.size,
        browserFile,
      });
    },
    [handleFileSelection],
  );

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      setDragging(false);
      const browserFile = event.dataTransfer.files[0];
      if (browserFile) handleBrowserInput(browserFile);
    },
    [handleBrowserInput],
  );

  const handleTranscribe = useCallback(async () => {
    const preflightError =
      missingConfigReason ||
      (openwhisprTooLarge
          ? 'OpenWhispr Cloud uploads are limited to 500 MB.'
          : '');

    await transcribe({
      mode,
      cloudProvider,
      groqProcessingMode,
      cloudModel: cloudModel || getDefaultCloudModel(cloudProvider),
      localProvider,
      activeLocalModel,
      activeApiKey,
      language,
      normalizedCustomUrl,
      noteTitle,
      noteFolder: selectedFolder,
      preflightError,
    });
  }, [
    activeApiKey,
    activeLocalModel,
    cloudModel,
    cloudProvider,
    groqProcessingMode,
    language,
    localProvider,
    missingConfigReason,
    mode,
    noteTitle,
    normalizedCustomUrl,
    openwhisprTooLarge,
    selectedFolder,
    transcribe,
  ]);

  const handleSaveNote = useCallback(() => {
    void persistCurrentNote();
  }, [persistCurrentNote]);


  const handleFolderChange = useCallback(
    (value: string) => {
      if (value !== '__new') {
        setSelectedFolder(value);
        return;
      }

      const nextFolder = window.prompt('New folder name');
      const trimmed = nextFolder?.trim();
      if (!trimmed) return;

      setFolders(state => [...new Set([...state, trimmed])].sort());
      setSelectedFolder(trimmed);
    },
    [],
  );

  const transcribingMsg = () => {
    if (mode === 'local') {
      return localProvider === 'nvidia'
        ? 'Transcribing locally with Parakeet...'
        : 'Transcribing locally with Whisper...';
    }

    if (cloudProvider === 'openwhispr') {
      return 'Transcribing via OpenWhispr Cloud...';
    }

    return `Transcribing via ${cloudProviderConfig.name}...`;
  };

  return (
    <>
      <title>NM | Upload</title>
      <div className="boxed-content" style={{ paddingTop: '28px' }}>

        {/* ── Header ── */}
        <div style={{ marginBottom: '1.75rem' }}>
          <h2 style={{ margin: '0 0 4px', fontSize: '1.2rem', fontWeight: 600, letterSpacing: '-0.03em' }}>
            Upload Audio
          </h2>
          <p style={{ margin: 0, fontSize: '12px', color: 'var(--color-muted-foreground)', opacity: 0.7 }}>
            Transcribe audio or video with cloud or local AI
          </p>
        </div>

        <div style={{
          display: 'grid',
          gap: '20px',
          alignItems: 'start',
          gridTemplateColumns: '1fr 272px',
        }}>
          {/* ── Main content area ── */}
          <div>

            {/* Drop zone — idle or file selected */}
            {(status === 'idle' || status === 'selected') && (
              <div
                onDragOver={event => { event.preventDefault(); setDragging(true); }}
                onDragLeave={() => setDragging(false)}
                onDrop={onDrop}
                style={{
                  borderRadius: '12px',
                  border: `1.5px dashed ${dragging ? 'var(--color-primary)' : status === 'selected' ? 'var(--color-border-active)' : 'var(--color-border)'}`,
                  padding: '52px 40px',
                  minHeight: '260px',
                  background: dragging ? 'oklch(from var(--color-primary) l c h / 0.04)' : 'transparent',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  transition: 'border-color 150ms ease, background 150ms ease',
                  marginBottom: '14px',
                  position: 'relative',
                }}
              >
                {/* Hidden file input — triggered via label or programmatic click */}
                <input
                  ref={fileInputRef}
                  id="upload-audio-input"
                  type="file"
                  accept="audio/*,video/*"
                  style={{ display: 'none' }}
                  onChange={event => {
                    const browserFile = event.target.files?.[0];
                    if (browserFile) handleBrowserInput(browserFile);
                    event.target.value = '';
                  }}
                />

                {status === 'idle' ? (
                  /* Use <label> so clicking anywhere in the zone natively opens the file picker */
                  <label
                    htmlFor="upload-audio-input"
                    style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'pointer', width: '100%' }}
                  >
                    <div style={{
                      width: '52px',
                      height: '52px',
                      borderRadius: '14px',
                      background: 'var(--color-surface-2)',
                      border: '1px solid var(--color-border-subtle)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '24px',
                      marginBottom: '18px',
                    }}>
                      🎙
                    </div>
                    <p style={{ fontSize: '14px', fontWeight: 600, margin: '0 0 6px', letterSpacing: '-0.01em' }}>
                      Drop your audio file here
                    </p>
                    <p style={{ fontSize: '12px', color: 'var(--color-muted-foreground)', margin: '0 0 16px' }}>
                      or click to browse
                    </p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', justifyContent: 'center', marginBottom: '14px' }}>
                      {SUPPORTED_FORMATS.map(fmt => (
                        <span key={fmt} style={{
                          fontSize: '10px',
                          fontWeight: 500,
                          padding: '3px 8px',
                          borderRadius: '4px',
                          background: 'var(--color-surface-3)',
                          border: '1px solid var(--color-border-subtle)',
                          color: 'var(--color-muted-foreground)',
                          letterSpacing: '0.02em',
                          textTransform: 'uppercase',
                        }}>
                          {fmt}
                        </span>
                      ))}
                    </div>
                    <div style={{
                      display: 'inline-flex', alignItems: 'center',
                      fontSize: '11px', fontWeight: 500,
                      padding: '3px 10px', borderRadius: '20px',
                      background: 'var(--color-surface-3)',
                      border: '1px solid var(--color-border-subtle)',
                      color: 'var(--color-muted-foreground)',
                    }}>
                      {activeModelLabel}
                    </div>
                  </label>
                ) : (
                  <>
                    <div style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '12px',
                      background: 'oklch(from var(--color-primary) l c h / 0.1)',
                      border: '1px solid oklch(from var(--color-primary) l c h / 0.25)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '22px',
                      marginBottom: '16px',
                    }}>
                      🎵
                    </div>
                    <p style={{ fontSize: '14px', fontWeight: 600, margin: '0 0 5px', letterSpacing: '-0.01em', maxWidth: '320px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {selectedFile?.name}
                    </p>
                    <p style={{ fontSize: '12px', color: 'var(--color-muted-foreground)', margin: '0 0 10px' }}>
                      {formatFileSize(selectedFile?.sizeBytes)}
                    </p>
                    <div style={{
                      display: 'inline-flex', alignItems: 'center',
                      fontSize: '11px', fontWeight: 500,
                      padding: '3px 10px', borderRadius: '20px',
                      background: 'var(--color-surface-3)',
                      border: '1px solid var(--color-border-subtle)',
                      color: 'var(--color-muted-foreground)',
                    }}>
                      {activeModelLabel}
                    </div>
                    {/* Allow changing the selected file */}
                    <label
                      htmlFor="upload-audio-input"
                      style={{ marginTop: '14px', fontSize: '12px', color: 'var(--color-primary)', cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: '2px' }}
                    >
                      Change file
                    </label>
                  </>
                )}
              </div>
            )}

            {/* Action buttons + warnings when file selected */}
            {status === 'selected' && (
              <div style={{ marginBottom: '16px' }}>
                {missingConfigReason && (
                  <div style={{ padding: '10px 14px', borderRadius: '8px', fontSize: '12px', marginBottom: '10px', border: '1px solid var(--color-warning)', color: 'var(--color-warning)', background: 'oklch(from var(--color-warning) l c h / 0.06)', lineHeight: 1.55 }}>
                    {missingConfigReason}
                  </div>
                )}
                {byokNeedsOptimization && (
                  <div style={{ padding: '10px 14px', borderRadius: '8px', fontSize: '12px', marginBottom: '10px', border: '1px solid var(--color-warning)', color: 'var(--color-warning)', background: 'oklch(from var(--color-warning) l c h / 0.06)', lineHeight: 1.55 }}>
                    {cloudProvider === 'groq'
                      ? groqProcessingMode === 'direct'
                        ? 'This file is over 25 MB. Direct mode will keep Groq on the browser path, so Niuniu Media will optimize it locally and may split it into Groq-sized chunks in your tab.'
                        : 'This file is over 25 MB. Niuniu Media will upload it once, then let the backend worker split and transcribe the audio in Groq-sized chunks.'
                      : 'This file is over 25 MB. Niuniu Media will first optimize it to 16 kHz mono WAV in your browser before uploading. If it still exceeds the provider limit, switch to local mode or OpenWhispr Cloud.'}
                  </div>
                )}
                {openwhisprTooLarge && (
                  <div style={{ padding: '10px 14px', borderRadius: '8px', fontSize: '12px', marginBottom: '10px', border: '1px solid var(--color-destructive)', color: 'var(--color-destructive)', background: 'oklch(from var(--color-destructive) l c h / 0.06)', lineHeight: 1.55 }}>
                    OpenWhispr Cloud uploads are capped at 500 MB.
                  </div>
                )}
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    className="btn-primary"
                    onClick={handleTranscribe}
                    disabled={!canTranscribe}
                    style={{ fontSize: '13px', fontWeight: 600, padding: '9px 24px', borderRadius: '8px', cursor: 'pointer', border: 'none' }}
                  >
                    Transcribe
                  </button>
                  <button
                    onClick={resetUpload}
                    className="btn-ghost"
                    style={{ fontSize: '13px', padding: '9px 16px', borderRadius: '8px', cursor: 'pointer', border: '1px solid var(--color-border-subtle)' }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Transcribing progress */}
            {status === 'transcribing' && (
              <>
                <div
                  className="section-panel"
                  style={{ padding: '44px 40px', borderRadius: '12px', textAlign: 'center', marginBottom: noteText ? '12px' : '14px' }}
                >
                  <div style={{
                    width: '44px',
                    height: '44px',
                    borderRadius: '12px',
                    background: 'oklch(from var(--color-primary) l c h / 0.1)',
                    border: '1px solid oklch(from var(--color-primary) l c h / 0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '20px',
                    margin: '0 auto 18px',
                    animation: 'pulse-glow 2s ease-in-out infinite',
                  }}>
                    🎙
                  </div>
                  <p style={{ fontSize: '13px', fontWeight: 600, margin: '0 0 4px', letterSpacing: '-0.01em' }}>
                    {transcribingMsg()}
                  </p>
                  {selectedFile && (
                    <p style={{ fontSize: '11px', color: 'var(--color-muted-foreground)', margin: '0 0 24px' }}>
                      {selectedFile.name}
                    </p>
                  )}
                  {transcribingDetail && (
                    <p style={{ fontSize: '11px', color: 'var(--color-muted-foreground)', margin: '-14px 0 18px' }}>
                      {transcribingDetail}
                    </p>
                  )}
                  <div style={{
                    height: '3px',
                    borderRadius: '99px',
                    background: 'var(--color-border)',
                    maxWidth: '280px',
                    margin: '0 auto',
                    overflow: 'hidden',
                  }}>
                    <div style={{
                      height: '100%',
                      borderRadius: '99px',
                      width: `${Math.min(progress, 100)}%`,
                      background: 'var(--color-primary)',
                      transition: 'width 500ms ease',
                    }} />
                  </div>
                  <p style={{ fontSize: '11px', color: 'var(--color-muted-foreground)', margin: '10px 0 0', fontVariantNumeric: 'tabular-nums' }}>
                    {Math.round(progress)}%
                  </p>
                </div>

                {noteText && (
                  <div
                    className="section-panel"
                    style={{ padding: '16px 18px', borderRadius: '12px', marginBottom: '14px' }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', marginBottom: '10px' }}>
                      <div>
                        <p style={{ margin: 0, fontSize: '12px', fontWeight: 600, letterSpacing: '-0.01em' }}>
                          Live Transcript Preview
                        </p>
                        <p style={{ margin: '3px 0 0', fontSize: '11px', color: 'var(--color-muted-foreground)', lineHeight: 1.5 }}>
                          {mode === 'cloud' && cloudProvider === 'groq'
                            ? 'Completed Groq chunks are appended here as they return. The final pass still merges overlaps after the last chunk.'
                            : 'Transcript text will appear here once the current transcription pipeline can emit partial results.'}
                        </p>
                      </div>
                      <ProviderBadge mode={mode} cloud={cloudProvider} />
                    </div>
                    <textarea
                      value={noteText}
                      readOnly
                      rows={14}
                      style={{
                        width: '100%',
                        boxSizing: 'border-box',
                        fontFamily: 'inherit',
                        fontSize: '13px',
                        lineHeight: 1.8,
                        padding: '12px 14px',
                        borderRadius: '8px',
                        resize: 'vertical',
                        opacity: 0.96,
                      }}
                    />
                  </div>
                )}
              </>
            )}

            {/* Error state */}
            {status === 'error' && (
              <div style={{
                borderRadius: '10px',
                marginBottom: '14px',
                border: '1px solid var(--color-destructive)',
                background: 'oklch(from var(--color-destructive) l c h / 0.05)',
                overflow: 'hidden',
              }}>
                <div style={{ borderLeft: '3px solid var(--color-destructive)', padding: '20px 22px' }}>
                  <p style={{ fontSize: '13px', fontWeight: 600, margin: '0 0 5px', color: 'var(--color-destructive)' }}>
                    Transcription failed
                  </p>
                  <p style={{ fontSize: '12px', color: 'var(--color-muted-foreground)', margin: '0 0 18px', lineHeight: 1.55 }}>
                    {errorMsg}
                  </p>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    {selectedFile && (
                      <button
                        className="btn-primary"
                        onClick={handleTranscribe}
                        style={{ fontSize: '13px', fontWeight: 600, padding: '8px 20px', borderRadius: '8px', cursor: 'pointer', border: 'none' }}
                      >
                        Retry
                      </button>
                    )}
                    <button
                      onClick={resetUpload}
                      className="btn-ghost"
                      style={{ fontSize: '13px', padding: '8px 16px', borderRadius: '8px', cursor: 'pointer', border: '1px solid var(--color-border-subtle)' }}
                    >
                      Start Over
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Complete — transcript editor */}
            {status === 'complete' && selectedFile && (
              <div>
                {/* Success header */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '10px 14px',
                  borderRadius: '8px',
                  background: 'oklch(0.7 0.18 145 / 0.07)',
                  border: '1px solid oklch(0.7 0.18 145 / 0.2)',
                  marginBottom: '14px',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--color-success)' }}>✓ Done</span>
                    {audioDurationSec && (
                      <span style={{ fontSize: '11px', color: 'var(--color-muted-foreground)' }}>
                        {formatDuration(audioDurationSec)}
                      </span>
                    )}
                    <ProviderBadge mode={mode} cloud={cloudProvider} />
                  </div>
                  <button
                    onClick={resetUpload}
                    className="btn-ghost"
                    style={{ fontSize: '13px', cursor: 'pointer', border: 'none', color: 'var(--color-muted-foreground)', padding: '5px 12px', borderRadius: '6px' }}
                  >
                    + Upload Another
                  </button>
                </div>

                {/* Title input */}
                <input
                  type="text"
                  value={noteTitle}
                  onChange={event => setNoteTitle(event.target.value)}
                  placeholder="Note title"
                  style={{
                    width: '100%',
                    boxSizing: 'border-box',
                    fontSize: '15px',
                    fontWeight: 600,
                    padding: '10px 14px',
                    borderRadius: '8px',
                    marginBottom: '8px',
                    letterSpacing: '-0.01em',
                  }}
                />

                {/* Transcript textarea */}
                <textarea
                  value={noteText}
                  onChange={event => setNoteText(event.target.value)}
                  rows={16}
                  style={{
                    width: '100%',
                    boxSizing: 'border-box',
                    fontFamily: 'inherit',
                    fontSize: '13px',
                    lineHeight: 1.8,
                    padding: '12px 14px',
                    borderRadius: '8px',
                    resize: 'vertical',
                  }}
                />

                {/* Action row */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '10px' }}>
                  {noteSaveError && (
                    <p style={{ fontSize: '12px', flex: 1, margin: 0, color: 'var(--color-destructive)' }}>
                      {noteSaveError}
                    </p>
                  )}

                  <select
                    value={selectedFolder}
                    onChange={event => handleFolderChange(event.target.value)}
                    style={{ flex: 1 }}
                  >
                    <option value="">No folder</option>
                    {folders.map(folder => (
                      <option key={folder} value={folder}>{folder}</option>
                    ))}
                    <option value="__new">+ New Folder...</option>
                  </select>

                  <button
                    onClick={() => { void navigator.clipboard.writeText(noteText); }}
                    className="btn-ghost"
                    style={{ fontSize: '13px', padding: '6px 14px', borderRadius: '7px', cursor: 'pointer', border: '1px solid var(--color-border-subtle)', whiteSpace: 'nowrap' }}
                  >
                    Copy
                  </button>

                  {!savedNoteId ? (
                    <button
                      className="btn-primary"
                      onClick={handleSaveNote}
                      disabled={isSavingNote}
                      style={{ fontSize: '13px', fontWeight: 600, padding: '8px 18px', borderRadius: '8px', cursor: 'pointer', border: 'none', whiteSpace: 'nowrap' }}
                    >
                      {isSavingNote ? 'Saving…' : 'Save Note'}
                    </button>
                  ) : (
                    <>
                      <span style={{ fontSize: '12px', color: 'var(--color-success)', whiteSpace: 'nowrap' }}>Saved ✓</span>
                      <button
                        className="btn-primary"
                        onClick={() => navigate(Routes.TranscriptionNote(savedNoteId))}
                        style={{ fontSize: '13px', fontWeight: 600, padding: '8px 18px', borderRadius: '8px', cursor: 'pointer', border: 'none', whiteSpace: 'nowrap' }}
                      >
                        Open Note
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* ── Settings panel ── */}
          <div
            className="section-panel accent-bar"
            style={{ padding: '16px 18px', borderRadius: '10px', position: 'relative' }}
          >
            <p style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--color-muted-foreground)', margin: '0 0 18px', opacity: 0.65 }}>
              Transcription Settings
            </p>
            <SettingsPanel
              mode={mode}
              setMode={setMode}
              cloudProvider={cloudProvider}
              setCloudProvider={setCloudProvider}
              groqProcessingMode={groqProcessingMode}
              setGroqProcessingMode={setGroqProcessingMode}
              cloudModel={cloudModel}
              setCloudModel={setCloudModel}
              localProvider={localProvider}
              setLocalProvider={setLocalProvider}
              localModel={localModel}
              setLocalModel={setLocalModel}
              localParakeetModel={localParakeetModel}
              setLocalParakeetModel={setLocalParakeetModel}
              apiKeys={apiKeys}
              setApiKey={setApiKey}
              customUrl={customUrl}
              setCustomUrl={setCustomUrl}
              language={language}
              setLanguage={setLanguage}
              runtimeHint={runtimeHint}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default Upload;
